import { ApiErrorSchema, type ErrorCode } from '@jisy/shared';

/**
 * Обращения к API. Сервер возвращает код ошибки, а не готовую фразу: текст подставляет
 * интерфейс на языке пользователя. Здесь код лишь доводится до компонентов в удобном виде.
 */

export class ApiRequestError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string | undefined;
  readonly params: Record<string, string | number | boolean> | undefined;

  constructor(options: {
    code: ErrorCode;
    status: number;
    requestId?: string | undefined;
    params?: Record<string, string | number | boolean> | undefined;
    detail?: string | undefined;
  }) {
    super(options.detail ?? options.code);
    this.name = 'ApiRequestError';
    this.code = options.code;
    this.status = options.status;
    this.requestId = options.requestId;
    this.params = options.params;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | string[] | undefined>;
}

/**
 * Загрузка двоичного тела — картинка из буфера обмена или вложение задачи. Отдельно от
 * `apiRequest`, потому что там тело всегда сериализуется в JSON: файл в base64 внутри JSON
 * вырос бы на треть на ровном месте.
 *
 * `contentType` задаётся явно там, где заголовок не должен повторять тип файла: разбор тела
 * на сервере выбирается как раз по нему, и `application/json` у файла .json увёл бы запрос
 * не в тот обработчик.
 */
export async function apiUpload<T>(path: string, blob: Blob, contentType?: string): Promise<T> {
  const response = await fetch(new URL(path, window.location.origin), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      'content-type': contentType ?? blob.type ?? 'application/octet-stream',
    },
    body: blob,
  }).catch((cause: unknown) => {
    throw new ApiRequestError({
      code: 'JIRA_UNAVAILABLE',
      status: 0,
      detail: cause instanceof Error ? cause.message : String(cause),
    });
  });

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = ApiErrorSchema.safeParse(payload);
    throw parsed.success
      ? new ApiRequestError({
          code: parsed.data.error.code,
          status: response.status,
          requestId: parsed.data.error.requestId,
          params: parsed.data.error.params,
        })
      : new ApiRequestError({ code: 'INTERNAL', status: response.status });
  }

  return payload as T;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(path, window.location.origin);

  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      // Список уходит одним параметром через запятую — сервер понимает оба вида.
      url.searchParams.set(key, value.join(','));
      continue;
    }
    url.searchParams.set(key, String(value));
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      // Сессия живёт в httpOnly cookie, поэтому запрос обязан её нести.
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    // Сеть недоступна или запрос отменён — до сервера мы не дошли.
    throw new ApiRequestError({
      code: 'JIRA_UNAVAILABLE',
      status: 0,
      detail: cause instanceof Error ? cause.message : String(cause),
    });
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = ApiErrorSchema.safeParse(payload);
    if (parsed.success) {
      throw new ApiRequestError({
        code: parsed.data.error.code,
        status: response.status,
        requestId: parsed.data.error.requestId,
        params: parsed.data.error.params,
        detail: parsed.data.error.detail,
      });
    }
    throw new ApiRequestError({ code: 'INTERNAL', status: response.status });
  }

  return payload as T;
}
